import { useState, useCallback } from "react";
import { DepletionMonthlyStudent } from "@/hooks/useDepletionMonthly";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { StudentDepletionDetailModal } from "./StudentDepletionDetailModal";
import { getStatusColor } from "./statusColors";

export type SortFieldV2 = 'code' | 'estimated_hours_depletion_date' | 'estimated_8hours_date' | 'hours' | null;
export type SortDirection = 'asc' | 'desc';

interface Props {
  students: DepletionMonthlyStudent[];
  isLoading: boolean;
  sortField: SortFieldV2;
  sortDirection: SortDirection;
  onSort: (field: SortFieldV2) => void;
}

function DateCell({ dateStr }: { dateStr: string | null }) {
  if (!dateStr) return <span className="text-muted-foreground">—</span>;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = parseISO(dateStr);
    const isPastOrToday = date <= today;

    if (isPastOrToday) {
      return (
        <div className="flex flex-col gap-1">
          <Badge className="bg-destructive/15 text-destructive border-0 w-fit">⚠️ ตอนนี้</Badge>
          <span className="text-xs text-muted-foreground">
            {format(date, "d MMM yy", { locale: th })}
          </span>
        </div>
      );
    }

    return <span>{format(date, "d MMM yy", { locale: th })}</span>;
  } catch {
    return <span>{dateStr}</span>;
  }
}

function SortIcon({ field, sortField, sortDirection }: { field: SortFieldV2; sortField: SortFieldV2; sortDirection: SortDirection }) {
  if (sortField !== field) return <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
  return sortDirection === 'asc'
    ? <ChevronUp className="h-3.5 w-3.5" />
    : <ChevronDown className="h-3.5 w-3.5" />;
}

const TABLE_HEADS = ["รหัส/ชื่อ", "สถานะ", "วันหมดคอร์ส", "วันใกล้หมด", "ชม.คงเหลือ", "ผู้ดูแล", "หมายเหตุ"];

export function DepletionV2Table({ students, isLoading, sortField, sortDirection, onSort }: Props) {
  const [selectedStudentCode, setSelectedStudentCode] = useState<string | null>(null);

  const handleSort = useCallback((field: SortFieldV2) => {
    onSort(field);
  }, [onSort]);

  const SortableHead = ({ field, children }: { field: SortFieldV2; children: React.ReactNode }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
      </div>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {TABLE_HEADS.map(h => (
                <TableHead key={h}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: TABLE_HEADS.length }).map((_, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="border rounded-lg p-12 text-center text-muted-foreground">
        ไม่มีนักเรียนใกล้หมดคอร์สในช่วงเวลาที่เลือก
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead field="code">รหัส / ชื่อ</SortableHead>
            <TableHead>สถานะ</TableHead>
            <SortableHead field="estimated_hours_depletion_date">วันหมดคอร์ส (0 ชม.)</SortableHead>
            <SortableHead field="estimated_8hours_date">วันใกล้หมด (8 ชม.)</SortableHead>
            <SortableHead field="hours">ชม.คงเหลือ</SortableHead>
            
            <TableHead>ผู้ดูแล</TableHead>
            <TableHead>หมายเหตุ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map((student) => (
            <TableRow
              key={student.id}
              className="cursor-pointer"
              onClick={() => setSelectedStudentCode(student.student_code)}
            >
              <TableCell>
                <div className="font-medium">{student.student_code}</div>
                <div className="text-sm text-muted-foreground">
                  {student.nickname && <span className="font-medium">{student.nickname} · </span>}
                  {student.first_name} {student.last_name ?? ""}
                </div>
              </TableCell>
              <TableCell>
              {student.status ? (
                  <Badge className={cn("border font-normal", getStatusColor(student.status))}>{student.status}</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                <DateCell dateStr={student.estimated_hours_depletion_date} />
              </TableCell>
              <TableCell className="text-sm">
                <DateCell dateStr={student.estimated_8hours_date} />
              </TableCell>
              <TableCell>
                <span className={cn(
                  "font-medium text-sm",
                  student.hours !== null && student.hours < 0 && "text-destructive",
                  student.hours !== null && student.hours >= 0 && student.hours < 8 && "text-yellow-600"
                )}>
                  {student.hours !== null ? `${student.hours.toFixed(1)} ชม.` : "—"}
                </span>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {student.follow_by ?? "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                {student.note ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <StudentDepletionDetailModal
        studentCode={selectedStudentCode}
        studentName={students.find(s => s.student_code === selectedStudentCode)?.nickname
          ?? students.find(s => s.student_code === selectedStudentCode)?.first_name ?? null}
        onClose={() => setSelectedStudentCode(null)}
      />
    </div>
  );
}
